/**
 * 端末の傾きセンサー（DeviceOrientationEvent の beta/gamma）のラッパー。
 * compass.ts（alpha＝方位）と同じ「停止関数を返す」流儀、同じイベントに
 * 相乗りする（別のイベントリスナーを追加するだけなので compass.ts 側には
 * 手を入れない。iOSの許可状態はページ単位で共有されるため、許可要求も
 * compass.ts の requestCompassPermission をそのまま再利用できる）。
 *
 * パイバル（係留気球）計測での使い方:
 *   端末を縦向きに持ち、画面を自分の方へ向けたまま上端を紐の延長線（風船）
 *   に合わせて狙う。このとき beta（前後方向の傾き）が紐の鉛直からの傾き
 *   角にほぼ比例して動く。ただし「まっすぐ縦に構えたときの beta の値」は
 *   持ち方の癖で個人差が出るため、鉛直の見た目の傾き角そのもの
 *   （0=真上を指す・90=水平線を指す）へ変換するには基準合わせが要る。
 *   ここでは「水平線を狙ったときの beta 値」を1回だけ記録してもらい、
 *   そこからの差分で角度を出す方式にする（ui/pibal.ts 側で実施）。
 */

export type { CompassSupport } from './compass.ts';
export { detectCompassSupport, requestCompassPermission } from './compass.ts';

export interface TiltReading {
  /** 前後方向の傾き [-180,180) */
  betaDeg: number;
  /** 左右方向の傾き [-90,90) */
  gammaDeg: number;
}

interface OrientationLike {
  beta: number | null;
  gamma: number | null;
}

/** イベント1件から傾きを取り出す純粋関数（DOM無しでテストできるよう分離） */
export function readingFromOrientationEvent(e: OrientationLike): TiltReading | null {
  if (e.beta == null || e.gamma == null) return null;
  return { betaDeg: e.beta, gammaDeg: e.gamma };
}

/**
 * 傾きの継続購読を開始する。戻り値の関数を呼ぶと停止する。
 * compass.ts の subscribeCompassHeading と同じ構造。
 */
export function subscribeTilt(
  onReading: (r: TiltReading) => void,
  onError: (message: string) => void,
): () => void {
  if (typeof DeviceOrientationEvent === 'undefined') {
    onError('この端末・ブラウザは傾きセンサーに対応していません');
    return () => {};
  }
  const handler = (e: Event): void => {
    const r = readingFromOrientationEvent(e as unknown as OrientationLike);
    if (r) onReading(r);
  };
  const eventName = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
  window.addEventListener(eventName, handler);
  return () => window.removeEventListener(eventName, handler);
}
